import classNames from 'classnames';
import Image, { StaticImageData } from 'next/image';
import type { userAgent } from 'next/server';
import { useEffect, useState } from 'react';
import Radio from '@alltrails/denali/components/Radio';
import PaypalLogo from '../../CheckoutBase/paypal-logo.svg';
import { payPalSupportedCurrencies } from '../payPalSupportedCurrencies';
import ApplePayLogo from './apple-pay-logo.svg';
import CreditCardLogos from './credit-card-logos.svg';
import GooglePayLogo from './google-pay-logo.svg';
import styles from './RadioPaymentForm.module.scss';

export enum PaymentOptionValues {
  Apple = 'Apple Pay',
  CreditCard = 'Credit card',
  PayPal = 'Paypal',
  GooglePay = 'Google Pay'
}

// Provided payment options, order based on browser data.
const getPaymentOptions = (
  isMobile: boolean,
  currencyCode: string,
  isGooglePayAvailable: boolean,
  browserName?: string,
  isApplePayAvailable?: boolean,
  isIosNativeApp?: boolean
): {
  value: PaymentOptionValues;
  icon: StaticImageData;
}[] => {
  const lowerCaseBrowserName = browserName?.toLowerCase();

  const apple = {
    value: PaymentOptionValues.Apple,
    icon: ApplePayLogo,
    isAvailable: isApplePayAvailable
  };

  const paypal = {
    value: PaymentOptionValues.PayPal,
    icon: PaypalLogo,
    // Prevent PayPal from being shown in the iOS webview (webkit browser)
    isAvailable: payPalSupportedCurrencies.includes(currencyCode) && browserName !== 'WebKit'
  };

  const google = {
    value: PaymentOptionValues.GooglePay,
    icon: GooglePayLogo,
    isAvailable: isGooglePayAvailable
  };

  const creditCard = {
    value: PaymentOptionValues.CreditCard,
    icon: CreditCardLogos,
    isAvailable: true
  };

  let orderedOptions = [creditCard, apple, google, paypal];

  if (isMobile && (lowerCaseBrowserName?.includes('safari') || browserName === 'WebKit')) {
    // Hide paypal if any treatment variant is enabled for the iOS web checkout optimization experiment
    if (isIosNativeApp) {
      orderedOptions = [apple, creditCard];
    } else {
      orderedOptions = [apple, creditCard, google, paypal];
    }
  }

  if (lowerCaseBrowserName?.includes('chrome')) {
    orderedOptions = [creditCard, google, paypal, apple];
  }

  return orderedOptions.filter(({ isAvailable }) => isAvailable);
};

type Props = {
  isMobile: boolean;
  isGooglePayAvailable: boolean;
  isApplePayEnabled?: boolean;
  currencyCode: string;
  browserName?: ReturnType<typeof userAgent>['browser']['name'];
  isIosNativeApp?: boolean;
  shouldHideRadioForm?: boolean;
  selectedPaymentType: PaymentOptionValues | undefined;
  handleChange: (value: PaymentOptionValues) => void;
};

const PaymentOptions = ({
  isMobile,
  isGooglePayAvailable,
  isApplePayEnabled = true,
  currencyCode,
  browserName,
  isIosNativeApp,
  shouldHideRadioForm = false,
  selectedPaymentType,
  handleChange
}: Props) => {
  const [isApplePayAvailable, setIsApplePayAvailable] = useState<boolean | undefined>();
  const paymentOptions = getPaymentOptions(
    isMobile,
    currencyCode,
    isGooglePayAvailable,
    browserName,
    isApplePayAvailable && isApplePayEnabled,
    isIosNativeApp
  );

  useEffect(() => {
    if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
      setIsApplePayAvailable(true);
    } else {
      setIsApplePayAvailable(false);
    }
  }, [currencyCode]);

  // Set the default payment option to the first available option.
  useEffect(() => {
    if (isApplePayAvailable !== undefined && !selectedPaymentType) {
      handleChange(paymentOptions[0]?.value);
    }
  }, [handleChange, paymentOptions, selectedPaymentType, isApplePayAvailable]);

  return paymentOptions.map(({ value, icon }) => (
    <div className={classNames(styles.paymentOption, { [styles.hidden]: shouldHideRadioForm })} key={value}>
      <Radio
        key={value}
        labelElement={<Image alt="" src={icon} height={30} />}
        value={value}
        selected={selectedPaymentType === value}
        onChange={() => handleChange(value)}
        testId={`payment-option-${value}`}
        className={styles.radioLabel}
      />
    </div>
  ));
};

export default PaymentOptions;
